import api from "./api";
import { Employee } from "../types/employee";

interface ApiEmployee {
  id: number | string;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
  calling_code?: string | null;
  status?: string | null;
  role?: string | null;
  group?: string | null;
  employee_group?: string | null;
  image?: string | null;
  user_image_url?: string | null;
  address?: string | null;
  country?: string | null;
  formatted_mobile?: string | null;
  services?: string[];
  locations?: Array<{ id?: number | string; name?: string } | string>;
  role_options?: Array<{ id: number; name: string; selected?: boolean }> | null;
  action?: {
    edit?: string;
    login_as_employee?: number;
    view_timesheet?: number;
    delete?: number;
  } | [];
  regular_schedules?: any[];
  schedule_overrides?: any[];
  [key: string]: unknown;
}

interface ApiEmployeesResponse {
  success: boolean;
  data: ApiEmployee[];
  message?: string;
}

const mapApiEmployee = (item: ApiEmployee): Employee => {
  const fullName =
    item.name ||
    [item.first_name, item.last_name].filter(Boolean).join(" ").trim();

  const mobile = item.mobile ?? item.phone ?? null;
  const image = item.user_image_url ?? item.image ?? "";
  const parsedId =
    typeof item.id === "number" ? item.id : Number.parseInt(String(item.id), 10);

  return {
    id: Number.isNaN(parsedId) ? 0 : parsedId,
    name: fullName || "Unnamed",
    email: item.email ?? "",
    phone: item.phone ?? undefined,
    mobile,
    calling_code: item.calling_code ?? null,
    status: item.status ?? null,
    role: item.role ?? "",
    group: item.group ?? item.employee_group ?? null,
    services: Array.isArray(item.services)
      ? item.services.map((service) => String(service))
      : [],
    locations: Array.isArray(item.locations) ? item.locations : [],
    image,
    user_image_url: image || null,
    formatted_mobile: item.formatted_mobile ?? null,
    address: item.address ?? undefined,
    country: item.country ?? undefined,
    role_options: Array.isArray(item.role_options)
      ? item.role_options.map((option) => ({
        id: option.id,
        name: option.name,
        selected: Boolean(option.selected),
      }))
      : null,
    action: item.action ?? [],
    regular_schedules: item.regular_schedules ?? [],
    schedule_overrides: item.schedule_overrides ?? [],
  };
};

export const getEmployees = async (): Promise<Employee[]> => {
  try {
    const response = await api.get<any>("/admin/employees");
    const payload = response.data;

    console.log("Employees API response:", payload);

    // Handle different response structures
    let employeesArray: ApiEmployee[] = [];

    if (payload?.success && Array.isArray(payload.data)) {
      // Standard structure: {success: true, data: [...]}
      employeesArray = payload.data;
    } else if (Array.isArray(payload?.employees)) {
      // Alternative structure: {employees: [...]}
      employeesArray = payload.employees;
    } else if (Array.isArray(payload?.data?.employees)) {
      // Nested structure: {data: {employees: [...]}}
      employeesArray = payload.data.employees;
    } else if (Array.isArray(payload)) {
      // Direct array response
      employeesArray = payload;
    } else {
      console.warn("Employees API response structure unexpected:", payload);
      return [];
    }

    const mapped = employeesArray.map(mapApiEmployee);
    console.log("Mapped employees:", mapped);
    return mapped;
  } catch (error: any) {
    console.error("Error fetching employees:", error);
    console.error("Error response:", error.response?.data);
    return [];
  }
};

export interface EmployeeDetailsResponse {
  employee: Employee;
  statistics: {
    completed_bookings: number;
    approved_bookings: number;
    pending_bookings: number;
    canceled_bookings: number;
    in_progress_bookings: number;
    total_earning: number;
  };
  sales_data: {
    labels: string[];
    data: number[];
  };
}

export const getEmployeeById = async (id: string | number): Promise<Employee | null> => {
  try {
    // Attempt 1: Fetch direct resource
    const response = await api.get<any>(`/admin/employees/${id}`);
    const payload = response.data;

    if (payload) {
      // 1. { success: true, data: { ...employee } }
      if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
        if (payload.data.data && !Array.isArray(payload.data.data)) {
          return mapApiEmployee(payload.data.data);
        }
        return mapApiEmployee(payload.data);
      }

      // 2. { employee: { ... } } - Matches user payload
      if (payload.employee && typeof payload.employee === 'object') {
        return mapApiEmployee(payload.employee);
      }

      // 3. Direct object
      if (payload.id || payload.name) {
        return mapApiEmployee(payload);
      }
    }

    // Attempt 2: Fallback to list search if direct fetch returns no useful data
    const allEmployees = await getEmployees();
    return allEmployees.find(e => String(e.id) === String(id)) || null;

  } catch (error: any) {
    console.warn(`Direct fetch for employee ${id} failed, trying list fallback...`, error.message);

    // Attempt 3: Fallback to list search on exception (e.g., 404)
    try {
      const allEmployees = await getEmployees();
      return allEmployees.find(e => String(e.id) === String(id)) || null;
    } catch (fallbackError) {
      return null;
    }
  }
};

export const getEmployeeFullDetails = async (id: string | number): Promise<EmployeeDetailsResponse | null> => {
  try {
    const response = await api.get<any>(`/admin/employees/${id}`);
    const payload = response.data;

    if (payload && payload.employee) {
      return {
        employee: mapApiEmployee(payload.employee),
        statistics: payload.statistics || {
          completed_bookings: 0,
          approved_bookings: 0,
          pending_bookings: 0,
          canceled_bookings: 0,
          in_progress_bookings: 0,
          total_earning: 0,
        },
        sales_data: payload.sales_data || {
          labels: [],
          data: [],
        },
      };
    }

    // Fallback: If payload structure is different, try to get at least the employee
    const employee = await getEmployeeById(id);
    if (employee) {
      return {
        employee,
        statistics: {
          completed_bookings: 0,
          approved_bookings: 0,
          pending_bookings: 0,
          canceled_bookings: 0,
          in_progress_bookings: 0,
          total_earning: 0,
        },
        sales_data: {
          labels: [],
          data: [],
        },
      };
    }

    return null;
  } catch (error: any) {
    console.error(`Error fetching full employee details ${id}:`, error);
    return null;
  }
};
